import { useQuery } from '@apollo/client';
import { FaDownload } from 'react-icons/fa';
import Papa from 'papaparse';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorText from '../../components/ErrorText';
import UsersTable from '../../components/admin/UsersTable';
import { ALL_USERS_QUERY } from '../../queries';
import { useActionModal } from '../../hooks/useActionModal';
import Button from '../../components/inputs/Button';
import { formatDateCellCustom } from '../../helpers/reactTable';

// Same role labels the UsersTable badges render.
const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  'finance-admin': 'Finance Admin',
  'super-user': 'Super User',
  'super-user-pii': 'Super User (PII)',
  'standard-user': 'Standard User',
  'view-only': 'View Only',
  'activations-user': 'Activations',
  'model-files-user': 'Alliant Data Sources',
};

const formatRolesForCSV = (roles: string[] | null): string => {
  if (!roles || roles.length === 0) return 'N/A';
  return roles.map((role) => ROLE_LABELS[role] || role).join(', ');
};

export default function AdminUsers() {
  const { loading, error, data } = useQuery(ALL_USERS_QUERY);
  const users = data?.users;

  const { state: actionState, dispatch: dispatchAction } = useActionModal();

  const handleDownload = () => {
    if (!users || users.length === 0) {
      alert('No users to download');
      return;
    }

    const csvData = users.map((user) => ({
      Roles: formatRolesForCSV(user.roles),
      Email: user.email,
      Status: user.disabled ? 'Disabled' : 'Active',
      'Created On': formatDateCellCustom({
        value: user.createdAt,
        ifEmpty: 'N/A',
        pattern: 'MM/dd/yyyy HH:mm:ss',
      }),
      'Last Login': formatDateCellCustom({
        value: user.lastLogin,
        ifEmpty: "Hasn't logged in",
        pattern: 'MM/dd/yyyy HH:mm:ss',
      }),
      'PWD Last Reset': formatDateCellCustom({
        value: user.passwordReset,
        ifEmpty: 'N/A',
        pattern: 'MM/dd/yyyy HH:mm:ss',
      }),
    }));

    const csv = Papa.unparse(csvData);

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);

    const dateStr = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    link.setAttribute('download', `users_export_${dateStr}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center mb-2">
        <Button
          type="button"
          primary
          small
          onClick={handleDownload}
          className="flex items-center space-x-2"
        >
          <FaDownload size={14} />
          <span>Download CSV</span>
        </Button>
      </div>
      {loading && <LoadingSpinner />}
      {error && <ErrorText className="mt-2" message={error.message} />}
      {users && (
        <UsersTable
          users={users}
          usersQuery={ALL_USERS_QUERY}
          actionState={actionState}
          dispatchAction={dispatchAction}
        />
      )}
    </div>
  );
}
